import React, { useEffect, useCallback, useMemo } from 'react'
import { TimeStepOptions, TimePicker } from '@mui/x-date-pickers-pro'
import { FormItem } from 'hic-components'
import { Stack, Box } from '@vapor/material'
import { ResourceTypeTimeUnitOfMeasurement } from 'src/api/api.generated'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'

type TimeRangePicker = {
  start: Date // | null
  end: Date // | null
}

type HicTimeRangePickerProps = {
  // The range values
  value: TimeRangePicker | null
  // onChange function
  onChange?: (value: TimeRangePicker | undefined) => void
  // Activity range to limit start and end time
  activity?: { from: Date; to: Date }
  // Properties of start control
  startProps?: {
    startLabel?: string
    startTooltip?: string
    startPlaceholder?: string
    startDateError?: string
  }
  // Properties of end control
  endProps?: {
    endLabel?: string
    endTooltip?: string
    endPlaceholder?: string
    endDateError?: string
  }
  // Unit of measure
  timeUnitOfMeasurement?: ResourceTypeTimeUnitOfMeasurement
  fieldSmall?: true
  maxWidth?: string
  errors: boolean
  disabled?: boolean
}

const HicTimeRangePicker = React.forwardRef<HTMLInputElement, HicTimeRangePickerProps>((props: HicTimeRangePickerProps, ref) => {
  const { value, startProps, endProps, timeUnitOfMeasurement, activity, fieldSmall, maxWidth, errors, disabled } = props
  const formatDate = useFormatDate()

  const timeStep = timeUnitOfMeasurement ? getTimeResourceTypeTimeStep(timeUnitOfMeasurement) : undefined

  const onDateChange = useCallback(
    (dateChanged: 'start' | 'end', updDate: Date) => {
      if (props.onChange) {
        if (dateChanged === 'start') props.onChange({ start: updDate, end: props.value?.end || updDate })
        else props.onChange({ start: props.value?.start || updDate, end: updDate })
      }
    },
    [props.onChange, props.value],
  )

  const minTime = useMemo(() => activity?.from || new Date('1970-01-01:00:00:00'), [activity])
  const maxTime = useMemo(() => activity?.to || new Date('1970-01-01:23:59:00'), [activity])

  // useEffect(() => {
  //   if (props.onChange && (+(value?.start ?? 0) !== +minTime || +(value?.end ?? 0) !== +maxTime)) {
  //     props.onChange({ start: minTime, end: maxTime })
  //   }
  // }, [activity, minTime, maxTime])

  // switch (timeUnitOfMeasurement) {
  //   case 'QUARTER':
  //   case 'HALF_HOUR':
  //   case 'HOUR':
  //   case 'HALF_DAY':
  return (
    <Stack direction="row" spacing={1}>
      <TimePicker
        // defaultValue={props.value?.start}
        value={props.value?.start}
        timeSteps={timeStep}
        onChange={(newValue, context) => {
          if (!context.validationError && newValue) onDateChange('start', newValue)
        }}
        label={startProps?.startLabel ? <FormItem fieldLabel={startProps?.startLabel} fieldTooltip={startProps?.startTooltip} /> : undefined}
        minTime={minTime}
        maxTime={maxTime}
        slotProps={{
          textField: () => ({
            InputProps: { label: '' /*, hiddenLabel: true*/ },
            InputLabelProps: { sx: { pb: 0, mb: '0px !important', lineHeight: '1.57 !important' } },
            hiddenLabel: true,
            placeholder: startProps?.startPlaceholder,
            helperText: startProps?.startDateError,
            error: errors,
            size: fieldSmall ? 'small' : undefined,
            sx: {
              maxWidth: maxWidth,
            },
          }),
        }}
        disabled={disabled}
      />
      <TimePicker
        // defaultValue={props.value?.end}
        value={props.value?.end}
        timeSteps={timeStep}
        disabled={(!props.value?.start ? true : false) || disabled}
        minTime={props.value?.start ?? undefined}
        maxTime={maxTime}
        onChange={(newValue, context) => {
          if (!context.validationError && newValue) onDateChange('end', newValue)
        }}
        label={endProps?.endLabel ? <FormItem fieldLabel={endProps?.endLabel} fieldTooltip={endProps?.endTooltip} /> : undefined}
        slotProps={{
          textField: () => ({
            InputProps: { label: '' /*, hiddenLabel: true*/ },
            InputLabelProps: { sx: { pb: 0, mb: '0px !important', lineHeight: '1.57 !important' } },
            hiddenLabel: true,
            placeholder: endProps?.endPlaceholder,
            helperText: endProps?.endDateError,
            error: errors,
            size: fieldSmall ? 'small' : undefined,
            sx: {
              maxWidth: maxWidth,
              ml: '8px !important',
            },
          }),
        }}
      />
    </Stack>
  )
  //   case 'DAY':
  //     return (
  //       <Stack direction="row" spacing={2}>
  //         <Box sx={{ lineHeight: 1.5 }}>
  //           <FormItem fieldLabel={startProps?.startLabel} fieldTooltip={startProps?.startTooltip} />
  //           <Box>{props.value?.start ? formatDate(props.value.start, 'p') : '-'}</Box>
  //         </Box>
  //         <Box sx={{ lineHeight: 1.5 }}>
  //           <FormItem fieldLabel={endProps?.endLabel} fieldTooltip={endProps?.endTooltip} />
  //           <Box>{props.value?.end ? formatDate(props.value.end, 'p') : '-'}</Box>
  //         </Box>
  //       </Stack>
  //     )
  //   default:
  //     return <></>
  // }
})

export default HicTimeRangePicker

const getTimeResourceTypeTimeStep = (type: ResourceTypeTimeUnitOfMeasurement): TimeStepOptions | undefined => {
  switch (type) {
    case 'QUARTER':
      return { hours: 1, minutes: 15 }
    case 'HALF_HOUR':
      return { hours: 1, minutes: 30 }
    case 'HOUR':
      return { hours: 1, minutes: 60 }
    case 'HALF_DAY':
      return { hours: 12, minutes: 60 }
    case 'DAY':
      return { hours: 12, minutes: 60 }
    default:
      break
  }
}
